/**
 * Утилиты для обработки ошибок API
 *
 * Бэкенд может возвращать ошибки в разных форматах:
 *
 * 1. Стандартный формат ApiDataResponse:
 *    { success: false, errorMessage: "...", errorCode: 400 }
 *
 * 2. Ошибки валидации ASP.NET (RFC 7807):
 *    {
 *      errors: { fieldName: ["error message 1", "error message 2"] },
 *      type: "https://tools.ietf.org/html/rfc7231#section-6.5.1",
 *      title: "One or more validation errors occurred.",
 *      status: 400,
 *      traceId: "..."
 *    }
 */

import { ApiDataResponse } from '../api/types/response';

/**
 * Формат ошибки валидации от ASP.NET (RFC 7807 Problem Details)
 */
export type ValidationErrorResponse = {
  errors: Record<string, string[]>;
  type?: string;
  title?: string;
  status?: number;
  traceId?: string;
};

/**
 * Человекочитаемые названия полей для ошибок валидации
 */
const FIELD_LABELS: Record<string, string> = {
  timeout: 'тайм-аут',
  weight: 'вес позиции',
  price: 'цена',
  stopLoss: 'стоп-лосс',
  takeProfit: 'тейк-профит',
  symbol: 'тикер',
  classCode: 'код класса',
  strategyId: 'ID стратегии',
  comment: 'комментарий',
  name: 'название',
  description: 'описание',
  email: 'email',
  password: 'пароль',
  phone: 'телефон',
};

/**
 * Получить человекочитаемое название поля
 */
const getFieldLabel = (fieldName: string): string => {
  // Приводим к нижнему регистру для сравнения (ASP.NET может возвращать разный регистр)
  const lowerFieldName = fieldName.toLowerCase();

  for (const [key, label] of Object.entries(FIELD_LABELS)) {
    if (key.toLowerCase() === lowerFieldName) {
      return label;
    }
  }

  // Если перевода нет, возвращаем оригинальное имя поля
  return fieldName;
};

/**
 * Проверяет, является ли ответ ошибкой валидации ASP.NET
 */
export const isValidationError = (response: unknown): response is ValidationErrorResponse => {
  if (!response || typeof response !== 'object') {
    return false;
  }

  const obj = response as Record<string, unknown>;

  return (
    'errors' in obj &&
    typeof obj.errors === 'object' &&
    obj.errors !== null &&
    (obj.status === 400 || obj.title === 'One or more validation errors occurred.')
  );
};

/**
 * Проверяет, является ли ответ стандартным ApiDataResponse с ошибкой
 */
export const isApiDataResponseError = <T>(response: unknown): response is ApiDataResponse<T> & { success: false } => {
  if (!response || typeof response !== 'object') {
    return false;
  }

  const obj = response as Record<string, unknown>;
  return 'success' in obj && obj.success === false;
};

/**
 * Форматирует ошибки валидации в читаемую строку
 */
const formatValidationErrors = (errors: Record<string, string[]>): string => {
  const messages: string[] = [];

  for (const [fieldName, fieldErrors] of Object.entries(errors)) {
    const label = getFieldLabel(fieldName);

    // Если есть конкретные сообщения об ошибках, используем их
    if (fieldErrors.length > 0) {
      // Проверяем, содержит ли сообщение техническую информацию (например JSON parsing error)
      const isTechnicalError = fieldErrors.some(
        (err) =>
          err.includes('JSON') ||
          err.includes('Path') ||
          err.includes('line') ||
          err.includes('position') ||
          err.includes('Int32') ||
          err.includes('Int64'),
      );

      if (isTechnicalError) {
        // Для технических ошибок показываем упрощённое сообщение
        messages.push(`Некорректное значение поля "${label}"`);
      } else {
        // Для понятных сообщений показываем как есть
        messages.push(`${label}: ${fieldErrors.join(', ')}`);
      }
    } else {
      messages.push(`Некорректное значение поля "${label}"`);
    }
  }

  return messages.join('. ');
};

/**
 * Извлекает читаемое сообщение об ошибке из любого типа ответа API
 *
 * @param response - Ответ от API (может быть ApiDataResponse, ValidationErrorResponse или unknown)
 * @param defaultMessage - Сообщение по умолчанию, если не удалось распарсить ошибку
 * @returns Читаемое сообщение об ошибке
 */
export const parseApiError = (
  response: unknown,
  defaultMessage: string = 'Произошла непредвиденная ошибка',
): string => {
  // Случай 1: Ошибка валидации ASP.NET
  if (isValidationError(response)) {
    const formattedErrors = formatValidationErrors(response.errors);
    return formattedErrors || response.title || defaultMessage;
  }

  // Случай 2: Стандартный ApiDataResponse с ошибкой
  if (isApiDataResponseError(response)) {
    return response.errorMessage || defaultMessage;
  }

  // Случай 3: Объект с полем message (Error или похожий)
  if (response && typeof response === 'object' && 'message' in response) {
    const msg = (response as { message: unknown }).message;
    if (typeof msg === 'string' && msg) {
      try {
        const parsed = JSON.parse(msg);
        if (parsed && typeof parsed === 'object') {
          if (parsed.statusText && parsed.status) {
            return `Ошибка сервера: ${parsed.statusText} (${parsed.status})`;
          }
          if (parsed.message) {
            return parsed.message;
          }
        }
      } catch {
        // не JSON — возвращаем как есть
      }
      return msg;
    }
  }

  // Случай 4: Строка
  if (typeof response === 'string' && response) {
    try {
      const parsed = JSON.parse(response);
      if (parsed && typeof parsed === 'object' && parsed.statusText && parsed.status) {
        return `Ошибка сервера: ${parsed.statusText} (${parsed.status})`;
      }
    } catch {
      // не JSON
    }
    return response;
  }

  return defaultMessage;
};

/**
 * Проверяет, является ли ответ успешным ApiDataResponse
 */
export const isSuccessResponse = <T>(response: unknown): response is ApiDataResponse<T> & { success: true } => {
  if (!response || typeof response !== 'object') {
    return false;
  }

  const obj = response as Record<string, unknown>;
  return 'success' in obj && obj.success === true;
};

/**
 * Проверяет, является ли ответ ошибочным (валидация или ApiDataResponse с success: false)
 */
export const isErrorResponse = (response: unknown): boolean => {
  return isValidationError(response) || isApiDataResponseError(response);
};
